import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSessionToken } from "../../hooks/useSession.js";
import { cn } from "../../lib/cn.js";
import { Button } from "../ui/Button.js";
import { PageHeader } from "../ui/PageHeader.js";
import { LoadingStatus, Skeleton } from "../ui/index.js";
import { ServicesOpsSkeleton } from "../skeletons/ServicesOpsSkeleton.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const ENROLLMENT_STATUSES = [
  "NOT_STARTED",
  "PENDING_PAYMENT",
  "PAID",
  "PROVISIONING",
  "READY",
  "FAILED",
  "REFUNDED",
] as const;
const TASK_STATUSES = ["QUEUED", "RUNNING", "SUCCEEDED", "FAILED"] as const;
const ORDER_STATUSES = ["PENDING", "PAID", "REFUNDED", "FAILED", "CANCELLED"] as const;

interface ServiceTask {
  id: string;
  type: string;
  status: string;
  attemptCount: number;
  sanitizedError: string | null;
  startedAt: string | null;
  updatedAt: string;
}

/** A task that's been sitting QUEUED without ever starting usually means the
 * worker never picked up its job (e.g. a Redis hiccup) — offer a retry once
 * it's been stale a while instead of only after an explicit FAILED status. */
const STUCK_QUEUED_THRESHOLD_MS = 5 * 60 * 1000;
function isStuckQueuedTask(task: ServiceTask) {
  if (task.status !== "QUEUED" || task.startedAt) return false;
  return Date.now() - new Date(task.updatedAt).getTime() > STUCK_QUEUED_THRESHOLD_MS;
}

interface ServiceEnrollment {
  id: string;
  product: string;
  status: string;
  failedAt: string | null;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  orders: Array<{ status: string; createdAt: string; paidAt: string | null; refundedAt: string | null }>;
  tasks: ServiceTask[];
  softPhone: { callDeliveryNumber: string } | null;
}

interface WorkerAttentionItem {
  id: string;
  kind: "surelc_producer" | "surelc_sync" | "submission";
  agent: { id: string; name: string; email: string };
  label: string;
  status: string;
  detail: string;
  retryable: boolean;
}

interface ServicesResponse {
  items: ServiceEnrollment[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  inventory: { available: number; assigned: number; total: number };
}

function label(value: string) {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function statusClass(status: string) {
  if (status === "READY" || status === "SUCCEEDED" || status === "PAID") {
    return "border-status-active-border bg-status-active-bg text-status-active-text";
  }
  if (status === "FAILED" || status === "REFUNDED" || status === "CANCELLED") {
    return "border-status-rejected-border bg-status-rejected-bg text-status-rejected-text";
  }
  return "border-status-pending-border bg-status-pending-bg text-status-pending-text";
}

function ServiceStatus({ status }: { status: string }) {
  return (
    <span className={cn("inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold", statusClass(status))}>
      {label(status)}
    </span>
  );
}

function FilterSelect({
  labelText,
  value,
  options,
  onChange,
}: {
  labelText: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1 text-xs font-medium text-content-secondary">
      <span>{labelText}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="focus-ring h-10 w-full rounded-lg border border-border-default bg-surface-overlay px-3 text-sm text-content-primary"
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>{label(option)}</option>
        ))}
      </select>
    </label>
  );
}

export function ServicesOps() {
  const token = useMemo(() => getSessionToken(), []);
  const [data, setData] = useState<ServicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [attention, setAttention] = useState<WorkerAttentionItem[] | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("");
  const [enrollmentStatus, setEnrollmentStatus] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: "25" });
      if (search) query.set("search", search);
      if (product) query.set("product", product);
      if (enrollmentStatus) query.set("enrollmentStatus", enrollmentStatus);
      if (taskStatus) query.set("taskStatus", taskStatus);
      if (orderStatus) query.set("orderStatus", orderStatus);
      const response = await fetch(`${API_BASE}/api/admin/services?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to load services");
      setData(payload.data as ServicesResponse);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load services");
    } finally {
      setLoading(false);
    }
  }, [enrollmentStatus, orderStatus, page, product, search, taskStatus, token]);

  const loadAttention = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/surelc-tasks/worker-attention`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to load worker errors");
      setAttention((payload.data?.items ?? []) as WorkerAttentionItem[]);
    } catch (caught) {
      setActionMessage(caught instanceof Error ? caught.message : "Unable to load worker errors");
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadAttention();
  }, [loadAttention]);

  function updateFilter(setter: (value: string) => void, value: string) {
    setPage(1);
    setter(value);
  }

  async function retryAttention(item: WorkerAttentionItem) {
    if (!token) return;
    setRetryingId(item.id);
    setActionMessage(null);
    const path =
      item.kind === "surelc_producer"
        ? `/api/surelc-tasks/producer-retry/${encodeURIComponent(item.agent.id)}`
        : `/api/submissions/${encodeURIComponent(item.id.slice("submission:".length))}/retry`;
    try {
      const response = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Retry could not be queued");
      setActionMessage(
        item.kind === "surelc_producer"
          ? "SureLC producer retry queued. It uses the current Create SureLC Producers flag."
          : "Contract job retry queued."
      );
      await loadAttention();
    } catch (caught) {
      setActionMessage(caught instanceof Error ? caught.message : "Retry could not be queued");
    } finally {
      setRetryingId(null);
    }
  }

  async function retry(path: string, id: string) {
    if (!token) return;
    setRetryingId(id);
    setActionMessage(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/services/${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Retry could not be queued");
      setActionMessage(payload.data?.alreadyQueued ? "Fulfillment was already queued." : "Fulfillment retry queued.");
      await load();
    } catch (caught) {
      setActionMessage(caught instanceof Error ? caught.message : "Retry could not be queued");
    } finally {
      setRetryingId(null);
    }
  }

  return (
    <section>
      <PageHeader
        title="Paid services"
        description="Monitor payment entitlement, inventory assignment, and fulfillment tasks. Worker jobs that stop short of going through are listed here so they can be retried."
      />

      <section className="mb-5 space-y-3" aria-labelledby="worker-attention-title">
        <h2 id="worker-attention-title" className="text-lg font-semibold text-content-primary">
          Needs a look
        </h2>
        {attention === null ? (
          <div role="status" aria-busy="true" className="space-y-2 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4">
            <LoadingStatus label="Loading worker errors" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : attention.length === 0 ? (
          <p className="text-sm text-content-muted">No SureLC or contract jobs are waiting on staff.</p>
        ) : (
          <div className="overflow-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="text-content-secondary">
                <tr>
                  {["Agent", "Job", "Error", "Action"].map((header) => (
                    <th key={header} className="py-2 pr-4 text-left font-medium">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attention.map((item) => (
                  <tr key={item.id} className="border-t border-border-subtle align-top">
                    <td className="py-3 pr-4">
                      <Link to={`/dashboard/agents/${item.agent.id}`} className="font-medium text-accent hover:underline">
                        {item.agent.name}
                      </Link>
                      <p className="text-xs text-content-muted">{item.agent.email}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-content-secondary">{item.label}</p>
                      <ServiceStatus status={item.status} />
                    </td>
                    <td className="max-w-md py-3 pr-4 text-xs text-status-rejected-text">{item.detail}</td>
                    <td className="py-3 pr-4">
                      {item.retryable ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={retryingId === item.id}
                          onClick={() => void retryAttention(item)}
                        >
                          {retryingId === item.id ? "Queuing…" : "Retry"}
                        </Button>
                      ) : (
                        <span className="text-xs text-content-muted">Open the agent</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {actionMessage && (
        <div role="status" className="mb-4 rounded-lg border border-border-default bg-surface-overlay px-4 py-3 text-sm text-content-secondary">
          {actionMessage}
        </div>
      )}
      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {loading && !data ? (
        <ServicesOpsSkeleton />
      ) : data ? (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ["Available numbers", data.inventory.available],
              ["Assigned numbers", data.inventory.assigned],
              ["Total inventory", data.inventory.total],
            ].map(([name, count]) => (
              <div key={name} className="rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
                <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">{name}</p>
                <p className="mt-2 text-2xl font-semibold text-content-primary">{count}</p>
              </div>
            ))}
          </div>

          <form
            className="grid gap-3 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 sm:grid-cols-2 lg:grid-cols-5"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSearch(searchInput.trim());
            }}
          >
            <label className="space-y-1 text-xs font-medium text-content-secondary">
              <span>Agent</span>
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Name or email"
                className="focus-ring h-10 w-full rounded-lg border border-border-default bg-surface-overlay px-3 text-sm text-content-primary"
              />
            </label>
            <FilterSelect labelText="Product" value={product} options={["DIALER_INTELELEAD", "INSURANCE_TOOLKIT"]} onChange={(value) => updateFilter(setProduct, value)} />
            <FilterSelect labelText="Enrollment" value={enrollmentStatus} options={ENROLLMENT_STATUSES} onChange={(value) => updateFilter(setEnrollmentStatus, value)} />
            <FilterSelect labelText="Payment" value={orderStatus} options={ORDER_STATUSES} onChange={(value) => updateFilter(setOrderStatus, value)} />
            <div className="flex items-end gap-2">
              <FilterSelect labelText="Task" value={taskStatus} options={TASK_STATUSES} onChange={(value) => updateFilter(setTaskStatus, value)} />
              <Button type="submit" size="sm" className="mb-0.5 h-9">Search</Button>
            </div>
          </form>

          <div className="overflow-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
            <table className="w-full min-w-[1080px] text-sm">
              <thead className="text-content-secondary">
                <tr>
                  {["Agent", "Product", "Enrollment", "Payment", "Fulfillment", "Call delivery", "Actions"].map((header) => (
                    <th key={header} className="py-2 pr-4 text-left font-medium">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((enrollment) => {
                  const retryableTasks = enrollment.tasks.filter(
                    (task) => task.status === "FAILED" || isStuckQueuedTask(task)
                  );
                  const needsEnrollmentRetry = Boolean(enrollment.failedAt || enrollment.status === "FAILED") && retryableTasks.length === 0;
                  return (
                    <tr key={enrollment.id} className="border-t border-border-subtle align-top">
                      <td className="py-3 pr-4">
                        <p className="font-medium text-content-primary">
                          {[enrollment.user.firstName, enrollment.user.lastName].filter(Boolean).join(" ") || "Unnamed agent"}
                        </p>
                        <p className="text-xs text-content-muted">{enrollment.user.email}</p>
                      </td>
                      <td className="py-3 pr-4 text-content-secondary">{label(enrollment.product)}</td>
                      <td className="py-3 pr-4"><ServiceStatus status={enrollment.status} /></td>
                      <td className="py-3 pr-4">
                        {enrollment.orders[0] ? <ServiceStatus status={enrollment.orders[0].status} /> : <span className="text-content-muted">No order</span>}
                      </td>
                      <td className="space-y-2 py-3 pr-4">
                        {enrollment.tasks.length === 0 ? (
                          <span className="text-content-muted">No task</span>
                        ) : enrollment.tasks.map((task) => (
                          <div key={task.id}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-content-secondary">{label(task.type)}</span>
                              <ServiceStatus status={task.status} />
                            </div>
                            {task.sanitizedError && (
                              <p className="mt-1 max-w-sm text-xs text-status-rejected-text">{task.sanitizedError}</p>
                            )}
                          </div>
                        ))}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs text-content-secondary">
                        {enrollment.softPhone?.callDeliveryNumber ?? "Unassigned"}
                      </td>
                      <td className="space-y-2 py-3 pr-4">
                        {retryableTasks.map((task) => (
                          <Button
                            key={task.id}
                            size="sm"
                            variant="secondary"
                            disabled={retryingId === task.id}
                            onClick={() => void retry(`tasks/${encodeURIComponent(task.id)}/retry`, task.id)}
                          >
                            {retryingId === task.id
                              ? "Queuing…"
                              : task.status === "QUEUED"
                                ? "Retry stuck task"
                                : "Retry task"}
                          </Button>
                        ))}
                        {needsEnrollmentRetry && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={retryingId === enrollment.id}
                            onClick={() => void retry(`enrollments/${encodeURIComponent(enrollment.id)}/retry`, enrollment.id)}
                          >
                            {retryingId === enrollment.id ? "Queuing…" : "Retry enrollment"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.items.length === 0 && (
              <p className="border-t border-border-subtle py-10 text-center text-sm text-content-muted">No service enrollments match these filters.</p>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-border-subtle pt-4 text-sm text-content-secondary">
              <span>{data.pagination.total} enrollment{data.pagination.total === 1 ? "" : "s"}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</Button>
                <span>Page {page} of {data.pagination.totalPages}</span>
                <Button size="sm" variant="secondary" disabled={page >= data.pagination.totalPages || loading} onClick={() => setPage((value) => value + 1)}>Next</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
